<script setup lang="ts">
  import type { MenuLink, ModulesLink } from '~/types';
  const { track } = useTracker();

  defineProps<{
    menus: MenuLink[];
    modules: ModulesLink[];
  }>();

  const route = useRoute();

  const activeMenu = (menu: MenuLink) => {
    return route.path === menu.href;
  };
  const activeModule = (module: ModulesLink) => {
    return route.path === module.href;
  };

  const sendTracking = (label: string) => {
    switch (label) {
      case 'Meus orçamentos':
        track('header_menu_meus_orcamentos');
        break;
      case 'Meus pedidos':
        track('header_menu_meus_pedidos');
        break;
      case 'Kits de energia solar':
        track('header_menu_kits_energia_solar');
        break;
      case 'Monte seu kit':
        track('header_menu_monte_kit');
        break;
      case 'Produtos avulsos':
        track('header_menu_produtos_avulsos');
        break;
    }
  };
</script>

<template>
  <div class="store-menu-application">
    <div class="menu-application-content">
      <div class="menus">
        <component
          :is="menu.linkTag"
          v-for="(menu, index) in menus"
          :key="index"
          :[menu.hrefProp]="menu.href"
          class="menu"
          :active="activeMenu(menu)"
          @click="sendTracking(menu.label)">
          <img v-if="menu.icon" class="icon" :src="menu.icon" alt="menu-icon" />
          <span class="label">
            {{ menu.label }}
            <div v-if="menu.tag" class="tag" :class="menu.tagClass">
              {{ menu.tag }}
            </div>
          </span>
        </component>
      </div>
      <div class="modules">
        <NuxtLink
          v-for="(module, index) in modules"
          :key="index"
          :to="module.href"
          class="module"
          :active="activeModule(module)"
          @click="sendTracking(module.label)">
          <img class="icon" :src="module.icon" alt="module-icon" />
          <span class="label">{{ module.label }}</span>
        </NuxtLink>
      </div>
    </div>
    <SolDivider thickness="x-small" />
  </div>
</template>

<style lang="scss">
  @mixin site-container {
    @apply w-full md:site:max-w-[1440px];
    @apply md:site:mx-auto;
  }

  .store-menu-application {
    @apply hidden md:site:flex flex-col items-center justify-between;
    @apply bg-neutral-high-pure;

    > .menu-application-content {
      @apply flex justify-between;
      @include site-container;

      > .menus {
        @apply flex;

        > .menu {
          @apply px-2xs py-4xs;
          @apply flex items-center gap-nano;

          &:hover {
            @apply bg-neutral-high-light bg-opacity-intense;
          }

          > .icon {
            @apply icon-size-medium;
          }

          &[active='true'] {
            @apply bg-neutral-high-light;

            > .label {
              @apply text-neutral-low-dark;
            }
          }

          > .label {
            @apply fonts-body-large-regular;
            @apply text-neutral-low-medium;
            @apply gap-2;

            > .tag {
              @apply inline fonts-body-small-regular ml-0.5;
              @apply bg-brand-secondary-medium;
              @apply px-3 py-1 rounded-[12px] gap-1;
            }
            .secondary {
              @apply bg-brand-secondary-medium;
            }
            .neutral {
              @apply bg-neutral-high-light;
            }
          }
        }
      }

      > .modules {
        @apply flex items-center;

        > .module {
          @apply flex items-center gap-nano;
          @apply px-2xs py-4xs;

          &:hover {
            @apply bg-neutral-high-light bg-opacity-intense;
          }

          > .icon {
            @apply icon-size-medium;
          }

          &[active='true'] {
            @apply bg-neutral-high-light;

            > .label {
              @apply text-neutral-low-dark;
            }
          }

          > .label {
            @apply fonts-body-large-regular;
            @apply text-neutral-low-medium;
          }
        }
      }
    }
  }
</style>
